import { useEffect } from 'react';

import { useSelectedItemsStore } from '@/stores/selected-items';

import { TClip } from '../types';

/**
 * Keeps the clip selection equal to what the list shows, since the bulk
 * actions (review state, sponsorship report) act on the stored ids and not on
 * the checkboxes on screen.
 *
 * - A change of filters starts the selection over: the clips picked before may
 *   no longer be listed, and acting on them would touch clips nobody can see.
 *   It is driven by the filters the query is built from, not by
 *   `useProgramBroadcastFilters.setFilters`, which is also called from effects
 *   and would drop the selection without the user doing anything.
 * - While "select all" is on, the cards are drawn checked from the flag alone,
 *   so the stored ids follow the list as more pages load.
 * - The store is shared with the schedule and segments pages, so leaving this
 *   one clears it.
 */
export function useClipSelectionSync(clipItems: TClip[] | null | undefined, filtersKey: string) {
  const dispatch = useSelectedItemsStore(state => state.dispatch);
  const toggleAllActived = useSelectedItemsStore(state => state.toggleAllActived);

  useEffect(() => {
    dispatch({ type: 'reset' });
  }, [dispatch, filtersKey]);

  useEffect(() => {
    if (!toggleAllActived) return;

    const ids = (clipItems ?? []).map(clip => clip.id);
    const current = useSelectedItemsStore.getState().include;
    const isSame = current.length === ids.length && current.every((id, i) => id === ids[i]);

    if (!isSame) dispatch({ type: 'include', payload: ids });
  }, [dispatch, toggleAllActived, clipItems]);

  useEffect(() => () => dispatch({ type: 'reset' }), [dispatch]);
}
